<style lang="scss" scoped>
.settings {
	@include container();
	&__section {
		padding-bottom: 70px;
		border-bottom: 1px solid #eee;
		margin-bottom: 30px;
		&:nth-of-type(5) {
			margin-bottom: 0;
			border-bottom: 0;
		}
	}
	&__year {
		margin-left: 20px;
	}
	&__row {
		@include grid();
	}
	&__left {
		@include grid-col(4);
		padding-right: 40px;
	}
	&__right {
		@include grid-col(8);
	}
	&__pad-bottom {
		margin-bottom: 30px;
	}
	& /deep/ .el-cascader {
		width: 100%;
	}
	& /deep/ .el-select {
		width: 100%;
	}
	.warning a {
		color: #eb4335 !important;
		font-weight: bold;
	}
}
</style>
<template lang="pug">
.settings
    .main-panel
        loading(v-if='!form && $apollo.loading')
        template(v-else-if='form')
            .main-panel__padding.settings__section
                .settings__row
                    .settings__left
                        h2 Domains and OU’s
                    .settings__right
                        el-card.box-card.settings__pad-bottom
                            .clearfix(slot='header')
                                span Students
                            el-form(label-position='top')
                                el-form-item(label='What domain do you use for students?')
                                    el-select(name='format', v-model='form.domain', placeholder='Select')
                                        el-option(v-for='item in domains', :key='item.id', :label='item.id', :value='item.id')
                                div(v-if="features.ou")
                                    el-form-item(label='Which organisational unit do you want to use to create and manage students?')
                                        organisation-selector(v-model='form.student_org_unit_id')
                                    student-ou-structure(v-if="features.ou_structure" v-model="form.student_org_unit_structure")
                        el-card.box-card
                            .clearfix(slot='header')
                                span Staff
                            el-form(label-position='top')
                                el-form-item(label='What domain do you use for staff?')
                                    el-select(name='format', v-model='form.domain_employee', placeholder='Select')
                                        el-option(v-for='item in domains', :key='item.id', :label='item.id', :value='item.id')
                                div(v-if='features.ou')
                                    el-form-item(label='Which organisational unit do you want to use to create and manage staff?')
                                        organisation-selector(v-model='form.employee_org_unit_id')
            .main-panel__padding.settings__section
                .settings__row
                    .settings__left
                        h2.mt-3 Rules
                        p Control which users are available from your MIS data. Create custom rules by filtering students by registration, house, and year groups.
                        p.warning
                            a(href="http://help.wonde.com/en/articles/3943851-managing-rules-during-google-sync-setup", target="_blank") If you’re managing over 1,000 student and staff users, click here for more information.
                    .settings__right
                        student-rules(v-model='student_rule_object', class="settings__pad-bottom")
                        employee-rules(v-model='employee_rule_object')
            .main-panel__padding.settings__section(v-if="features.account_crud")
                .settings__row
                    .settings__left
                        h2.mt-3 Create and manage accounts
                        p Fine tune your {{providerName}} integration.
                    .settings__right
                        el-card.settings__pad-bottom
                            .clearfix(slot='header')
                                span Creating accounts
                            el-form(label-position='top')
                                el-form-item(label="How would you like to create user accounts inside G Suite for Education?")
                                    el-radio(v-model='form.creation_mode', label='auto', border='', size='medium') Automatically with Wonde Google Sync
                                    el-radio(v-model='form.creation_mode', label='self', border='', size='medium') Manually directly within the Google admin console
                                template(v-if="form.creation_mode == 'auto'")
                                    el-form-item(label='Student email format (naming convention)')
                                        el-select(name='format', v-model='form.email_format_student', placeholder='Select')
                                            el-option(v-for='item in email_formats.student', :key='item.value', :label='item.label', :value='item.value')
                                    el-form-item(label='Staff email format (naming convention)')
                                        el-select(name='format', v-model='form.email_format_employee', placeholder='Select')
                                            el-option(v-for='item in email_formats.employee', :key='item.value', :label='item.label', :value='item.value')
                        el-card.settings__pad-bottom
                            .clearfix(slot='header')
                                span Suspending and deleting accounts
                            el-form(label-position='top')
                                el-form-item(label='How would you like to suspend and delete users?')
                                    el-radio(v-model='form.deletion_mode', label='auto', border='', size='medium') Automatically with Wonde Google Sync
                                    el-radio(v-model='form.deletion_mode', label='self', border='', size='medium') Manually directly within the Google admin console
                                template(v-if="form.deletion_mode=='auto'")
                                    el-form-item(:label='"When a user is removed from the MIS, how long should " + providerName + " Sync wait before suspending an account?"')
                                        el-input-number(:min='0', :max='365', size='medium', v-model='form.suspend_delay', e2e="suspend_days")
                                        |    {{ form.suspend_delay == 1 ? "day" : "days" }}
                                    el-form-item(label='Once suspended, how long should Google Sync wait before deleting an account?')
                                        el-input-number(:min='0', :max='365', size='medium', v-model='deletion_days')
                                        |    {{ deletion_days == 1 ? "day" : "days" }}
                                        el-input-number(:min='0', :max='20', size='medium', v-model='deletion_years').settings__year
                                        |    {{ deletion_years == 1 ? "year" : "years" }}
            .main-panel__padding.settings__section(v-if="features.account_crud")
                .settings__row
                    .settings__left
                        h2.mt-3 Google groups
                    .settings__right
                        el-card.settings__pad-bottom(v-if="features.group")
                            .clearfix(slot='header')
                                span Groups and group memberships
                            el-form(label-position='top')
                                el-form-item(label="How would you like to create new Google groups?")
                                    el-radio(v-model='form.group_create_mode', label='auto', border='', size='medium') Automatically with Wonde Google Sync
                                    el-radio(v-model='form.group_create_mode', label='self', border='', size='medium') Manually directly within the Google admin console
                                el-form-item(label="How would you like to create and manage group memberships?")
                                    el-radio(v-model='form.group_membership_mode', label='auto', border='', size='medium') Automatically with Wonde Google Sync
                                    el-radio(v-model='form.group_membership_mode', label='self', border='', size='medium') Manually directly within the Google admin console
                                el-form-item(label="How would you like to handle group membership removals?")
                                    el-radio(v-model='form.group_membership_deletion_mode', label='auto', border='', size='medium') Automatically with Wonde Google Sync
                                    el-radio(v-model='form.group_membership_deletion_mode', label='self', border='', size='medium') Manually directly within the Google admin console
            .main-panel__padding.settings__section(v-if="features.account_crud")
                .settings__row
                    .settings__left
                        h2.mt-3 Google Classroom
                    .settings__right
                        el-card.settings__pad-bottom(v-if="features.classroom")
                            .clearfix(slot='header')
                                span Google Classroom and class memberships
                            el-form(label-position='top')
                                el-form-item(label="How would you like to create new classes in Google Classroom?")
                                    el-radio(v-model='form.class_create_mode', label='auto', border='', size='medium') Automatically with Wonde Google Sync
                                    el-radio(v-model='form.class_create_mode', label='self', border='', size='medium') Manually directly within the Google admin console
                                el-form-item(label="How would you like to create and manage class memberships?")
                                    el-radio(v-model='form.class_membership_mode', label='auto', border='', size='medium') Automatically with Wonde Google Sync
                                    el-radio(v-model='form.class_membership_mode', label='self', border='', size='medium') Manually directly within the Google admin console
                                el-form-item(label="How would you like to handle class membership removals?")
                                    el-radio(v-model='form.class_membership_deletion_mode', label='auto', border='', size='medium') Automatically with Wonde Google Sync
                                    el-radio(v-model='form.class_membership_deletion_mode', label='self', border='', size='medium') Manually directly within the Google admin console
                                //- el-form-item(:label="`How should Wonde ${providerName} Sync handle timetables?`")
                                //-     el-radio(v-model='form.class_timetable_mode', label='auto', border='', size='medium') Maintain a timetable in a class's calendar
                                //-     el-radio(v-model='form.class_timetable_mode', label='self', border='', size='medium') The school will manage a timetable directly in {{ providerName }}

        slot(name="footer")
</template>
<script>
import gql from "graphql-tag";
import _ from "lodash";
import moment from "moment";
import StudentRules from "./StudentRules";
import StudentOuStructure from "./StudentOuStructure";
import OrganisationSelector from "./OrganisationSelector";
import EmployeeRules from "./EmployeeRules";
export default {
	components: {
		OrganisationSelector,
		StudentRules,
		EmployeeRules,
		StudentOuStructure
	},

	computed: {
		email_formats() {
			if (this.previewUser && this.form) {
				const calcFormatsWithLabels = (formatString, domain, extra = "") => {
					// Display data
					const first_name = _.deburr(this.previewUser.first_name),
						first_name_letters = first_name ? first_name.split("") : [""],
						last_name = _.deburr(this.previewUser.last_name),
						last_name_letters = last_name ? last_name.split("") : [""],
						number = 2,
						cohort_year = 2015,
						education_details = {
							admission_number: "123456789",
							learner_number: "123456",
							admission_date: moment(),
							year_of_admission: moment().year()
						};

					return {
						value: formatString,
						label: `${eval(formatString)
							.toLowerCase()
							.replace(/[^\.a-z0-9]/g, "")}@${domain}${extra}`
					};
				};

				const studentFormats = _.map(this.form.email_formats, formatString => calcFormatsWithLabels(formatString, this.form.domain));
				const employeeDomain = this.form.domain_employee || this.form.domain;
				const employeeFormats = _.map(this.form.email_formats, formatString => calcFormatsWithLabels(formatString, employeeDomain));
				const processed = { student: studentFormats, employee: employeeFormats };

				if (!_.some(studentFormats, { value: this.form.email_format_student })) {
					processed.student.push(calcFormatsWithLabels(this.form.email_format_student, this.form.domain, " (Custom)"));
				}

				if (!_.some(employeeFormats, { value: this.form.email_format_employee })) {
					processed.employee.push(calcFormatsWithLabels(this.form.email_format_employee, employeeDomain, " (Custom)"));
				}

				return processed;
			} else {
				return [];
			}
		}
	},

	apollo: {
		domains: {
			query: gql`
				{
					Domains(verified: true) {
						id
						primary
						verified
					}
				}
			`,
			update: r => r.Domains
		},
		previewUser: {
			query: gql`
				{
					User {
						first_name
						last_name
					}
				}
			`,
			update: r => r.User
		},
		form: {
			query: gql`
				{
					Settings {
						id
						creation_mode
						deletion_mode
						group_create_mode
						group_membership_mode
						class_create_mode
						class_membership_mode
						class_timetable_mode
						group_membership_deletion_mode
						class_membership_deletion_mode
						creation_groups
						deletion_delay
						suspend_delay
						student_active
						email_format_employee
						email_format_student
						employee_active
						employee_org_unit_id
						student_org_unit_id
						domain
						domain_employee
						email_formats
						student_provision_rules {
							group
							operator
							value
						}
						student_org_unit_structure
					}
				}
			`,
			fetchPolicy: "cache-and-network",
			update(r) {
				let settings = _.cloneDeep(r.Settings);
				this.student_rule_object.rules = _.cloneDeep(settings.student_provision_rules);
				this.student_rule_object.active = _.cloneDeep(settings.student_active);
				delete settings.student_provision_rules;
				delete settings.student_provision_rule_match;
				delete settings.student_active;
				this.employee_rule_object.active = _.cloneDeep(settings.employee_active);
				delete settings.employee_active;

				if (settings.deletion_delay) {
					let total = settings.deletion_delay;

					if (total < 365) {
						this.deletion_days = total;
						this.deletion_years = 0;
					} else {
						this.deletion_years = Math.floor(total / 365);
						this.deletion_days = total - this.deletion_years * 365;
					}
				}

				return settings;
			}
		}
	},

	methods: {
		save() {
			this.loading = true;
			this.$emit("Saving", this.loading);

			const yearDays = this.deletion_years * 365;
			this.form.deletion_delay = this.deletion_days + yearDays;

			const insert = _.pickBy(this.form, (value, key) => {
				return key !== "__typename" && key !== "id" && key !== "email_formats";
			});

			insert.student_provision_rules = _.map(this.student_rule_object.rules, rule => _.pickBy(rule, (value, key) => key !== "__typename"));
			insert.student_active = this.student_rule_object.active;
			insert.student_provision_rule_match = this.student_rule_object.match;
			insert.employee_active = this.employee_rule_object.active;
			insert.student_org_unit_structure = _.compact(insert.student_org_unit_structure);

			this.$apollo
				.mutate({
					mutation: gql`
						mutation SaveSettings($object: InputSettings!) {
							SaveSettings(object: $object) {
								creation_mode
								deletion_mode
								class_create_mode
								class_membership_mode
								class_timetable_mode
								group_membership_deletion_mode
								class_membership_deletion_mode
								creation_groups
								deletion_delay
								suspend_delay
								student_active
								email_format_employee
								email_format_student
								student_provision_rule_match
								employee_org_unit_id
								student_org_unit_id
								domain
								domain_employee
								employee_active
								student_provision_rules {
									group
									operator
									value
								}
								student_org_unit_structure
							}
						}
					`,
					variables: {
						object: insert
					}
				})
				.then(r => {
					this.loading = false;
					this.$emit("Saving", this.loading);
					this.$emit("Saved");
					this.$message.success("Settings saved");
					this.$root.$emit("directory-reload-required");
				})
				.catch(e => {
					this.loading = false;
					this.$emit("Saving", this.loading);
					this.$message.error("Failed to update settings");
				});
		}
	},

	data() {
		return {
			deletion_days: 0,
			deletion_years: 0,
			student_rule_object: {
				active: null,
				match: null,
				rules: null
			},
			employee_rule_object: {
				active: null
			},
			form: null,
			loading: false,
			value: ""
		};
	}
};
</script>
